import { ParsedPrincipalCaps } from "./oracles";
import { Replace } from "../../reducers/type-util/primitive";
import {
    ParsedAddCollateralTimelockArgsApi,
    ParsedCollateralTermsApi,
    ParsedUpdateAssetDataParamsApi,
    ParsedUpdateStrategySettingsParamsApi,
    ParsedExternalYieldSourceArgsApi,
    ParsedUpdateVaultSettingsParamsApi,
    UpdateCapsParamsApi,
    ParsedTimeLockApi,
    PaginatedTimeLockResponseApi
} from "../api/timelocks";
import { TimeLockStatus } from "../timelock";

export type ParsedAddCollateralTimelockArgs = Replace<
    ParsedAddCollateralTimelockArgsApi,
    {
        collateralTerms: ParsedCollateralTerms;
        updateAssetDataParams: ParsedUpdateAssetDataParams;
    }
>;

export type ParsedCollateralTerms = Replace<
    ParsedCollateralTermsApi,
    {
        terms: bigint[];
    }
>;

export type ParsedUpdateAssetDataParams = Replace<
    ParsedUpdateAssetDataParamsApi,
    {
        maxCollateralAllocationPct?: bigint;
    }
>;

export type ParsedUpdateStrategySettingsParams = Replace<
    ParsedUpdateStrategySettingsParamsApi,
    {
        liquidityBuffer?: number;
        interestFee?: number;
        originationFee?: number;
        principalFee?: number;
        originationCap?: number;
        externalYieldSourceArgs?: ParsedExternalYieldSourceArgs;
    }
>;

export type ParsedExternalYieldSourceArgs = ParsedExternalYieldSourceArgsApi;

export type ParsedUpdateVaultSettingsParams = Replace<
    ParsedUpdateVaultSettingsParamsApi,
    {
        maxEarlyUnstakeFeeCbps: number;
    }
>;

export type UpdateCapsParams = Replace<
    UpdateCapsParamsApi,
    {
        borrowCaps?: ParsedPrincipalCaps;
        supplyCaps?: ParsedPrincipalCaps;
        withdrawCaps?: ParsedPrincipalCaps;
    }
>;

/**
 * Frontend type for timelock update params.
 * Nested types use domain types.
 */
export enum TimeLockUpdateType {
    UpdateStrategyMarketInformation = "updateStrategyMarketInformation",
    AddCollateral = "addCollateral",
    UpdateLtv = "updateLtv",
    RemoveCollateral = "removeCollateral",
    UpdateApy = "updateApy",
    UpdateStrategySettings = "updateStrategySettings",
    UpdateVaultSettings = "updateVaultSettings",
    CapsUpdate = "updateCaps",
    UpdateCollateralAllocationPct = "updateCollateralAllocationPct"
}

/**
 * Domain type for timelock update params.
 * Nested types use domain types.
 */

export type ParsedTimeLockUpdateParams =
    | { type: TimeLockUpdateType.UpdateStrategyMarketInformation; params: string }
    | { type: TimeLockUpdateType.AddCollateral; params: ParsedAddCollateralTimelockArgs }
    | { type: TimeLockUpdateType.UpdateLtv; params: ParsedUpdateAssetDataParams }
    | { type: TimeLockUpdateType.RemoveCollateral; params: ParsedCollateralTerms }
    | { type: TimeLockUpdateType.UpdateApy; params: ParsedCollateralTerms }
    | { type: TimeLockUpdateType.UpdateStrategySettings; params: ParsedUpdateStrategySettingsParams }
    | { type: TimeLockUpdateType.UpdateVaultSettings; params: ParsedUpdateVaultSettingsParams }
    | { type: TimeLockUpdateType.CapsUpdate; params: UpdateCapsParams }
    | { type: TimeLockUpdateType.UpdateCollateralAllocationPct; params: ParsedUpdateAssetDataParams };

/**
 * Domain type for parsed timelock.
 * Uses domain type for update field.
 */
export type ParsedTimeLock = Replace<
    ParsedTimeLockApi,
    {
        status: TimeLockStatus;
        update: ParsedTimeLockUpdateParams;
    }
>;

/**
 * Domain type for parsed timelock response.
 * Uses domain type for timeLocks array.
 */
export type PaginatedTimeLockResponse = Replace<
    PaginatedTimeLockResponseApi,
    {
        timeLocks: ParsedTimeLock[];
    }
>;
